// src/components/DashboardPage.js
import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { fetchTA1, fetchTungstenFlows, fetchDims } from '../dataservice.js';
import { DIM_KEY_MAP, DIM_FIELDS } from '../utils.js';
import TopFilterBar from './TopFilterBar.js';
import StackedBarChart from './StackedBarChart.js';
import DataTable from './DataTable.js';
import FlowMap from './FlowMap.js';
import FlowTable from './FlowTable.js';
import { jsonToCsv, downloadFile } from '../utils';
import LandingPage from './LandingPage.js';
import MineralDropdown from './MineralDropdown.js';

const INITIAL_FILTERS = {
  yearMin: 2017, yearMax: 2030,
  Mineral: [], Region: [], Country: [], Form: [], Input: [], Performer: [],
  Scenario: '',
  Type: [],
  Units: [],
  value_type: 'Cost'
};
const INITIAL_DIMS = DIM_FIELDS.reduce((acc, f) => ({...acc, [f]: []}), {});

const facilityCountryMap = {
  "Xikuangshan": "China", "Longshan": "China", "Banxi": "China", "Sarylakh": "Russia", "Sentachan": "Russia", "Anzob": "Tajikistan",
  "Umicore Hoboken": "Belgium", "Yellow Pine": "United States", "Mutanda": "DR Congo", "Jinchuan Group": "China",
  "Umicore Kokkola": "Finland", "Greenbushes": "Australia", "Atacama": "Chile", "Ganfeng Lithium": "China", "Bayan Obo": "China",
  "Mountain Pass": "United States", "Lynas Malaysia": "Malaysia", "Stibnite": "United States", "Keban": "Turkey", "Gumushane": "Turkey",
  "Cerro Rico": "Bolivia", "Chorolque": "Bolivia", "Shan State": "Myanmar", "Kayah State": "Myanmar", "Hunan Chenzhou": "China",
  "Guangxi Nandan": "China", "Hunan Lengshuijiang": "China", "Keban Antimony": "Turkey", "GV Gold": "Russia",
  "Katanga": "DR Congo", "Kolwezi": "DR Congo", "Tenke Fungurume": "DR Congo", "Pomalaa": "Indonesia", "Sorowako": "Indonesia",
  "Norilsk": "Russia", "Pechenga": "Russia", "Murrin Murrin": "Australia", "Ravensthorpe": "Australia", "Palawan": "Philippines",
  "Surigao": "Philippines", "Ambatovy": "Madagascar", "Sherritt": "Madagascar", "Huayou Cobalt": "China", "GEM Co": "China",
  "Freeport Cobalt": "Finland", "Vale Sudbury": "Canada", "Glencore Sudbury": "Canada", "Norkyst": "Norway", "Glencore Nikkelverk": "Norway",
  "Pilbara": "Australia", "Mt Marion": "Australia", "Carmen": "Chile", "Maricunga": "Chile", "Qinghai": "China", "Tibet": "China",
  "Sichuan": "China", "Olaroz": "Argentina", "Sal de Vida": "Argentina", "Bikita": "Zimbabwe", "Arcadia": "Zimbabwe",
  "Tianqi Lithium": "China", "BYD": "China", "SQM": "Chile", "Albemarle": "Chile", "Orocobre": "Australia", "Galaxy Resources": "Australia",
  "Mount Weld": "Australia", "Bear Lodge": "United States", "Kachin State": "Myanmar", "Nolans": "Australia",
  "Southern Thailand": "Thailand", "China Northern Rare Earth": "China", "China Minmetals": "China", "Shenghe Resources": "China",
  "Lynas Advanced Materials": "Malaysia", "Silmet": "Estonia"
};

function addCountryDataToFlows(flows) {
  const getCountryFromConsumerName = (name, mineral) => {
    if (!mineral) return null;
    const suffix = ` ${mineral} Industries`;
    if (name.endsWith(suffix)) {
      return name.slice(0, -suffix.length);
    }
    return null;
  };

  return flows.map(flow => {
    const newFlow = { ...flow };
    newFlow.sourceCountry = facilityCountryMap[flow.source] || getCountryFromConsumerName(flow.source, flow.metal) || 'Unknown';
    newFlow.targetCountry = facilityCountryMap[flow.target] || getCountryFromConsumerName(flow.target, flow.metal) || 'Unknown';
    return newFlow;
  });
}

function applyFlowFilters(flows, f, flowSelection, ignore = []) {
  let filtered = flows.filter(flow => {
    if (f.yearMin && Number(flow.year) < Number(f.yearMin)) return false;
    if (f.yearMax && Number(flow.year) > Number(f.yearMax)) return false;

    if (!ignore.includes('Mineral') && f.Mineral?.length && !f.Mineral.includes(flow.metal)) {
      return false;
    }

    if (!ignore.includes('Country') && f.Country?.length) {
      if (!f.Country.includes(flow.sourceCountry) && !f.Country.includes(flow.targetCountry)) {
        return false;
      }
    }

    return true;
  });

  if (flowSelection && flowSelection.dim && flowSelection.value) {
    const dataKey = DIM_KEY_MAP[flowSelection.dim];
    if (dataKey) {
      if (flowSelection.dim === 'Country') {
        filtered = filtered.filter(flow => flow.sourceCountry === flowSelection.value || flow.targetCountry === flowSelection.value);
      } else if (flowSelection.dim === 'Stage') {
        filtered = filtered.filter(flow => `${flow.sourceStage} → ${flow.targetStage}` === flowSelection.value);
      } else {
        filtered = filtered.filter(flow => flow[dataKey] === flowSelection.value);
      }
    }
  }

  return filtered;
}

export default function DashboardPage(){
  const [raw, setRaw] = useState([]);
  const [benchmarkRows, setBenchmarkRows] = useState([]);
  const [dims, setDims] = useState(INITIAL_DIMS);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [selectedMineral, setSelectedMineral] = useState(null);

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [flowSelection, setFlowSelection] = useState({ dim: null, value: null });

  // Add state for chart settings
  const [chartSettings, setChartSettings] = useState({
    breakdownBy: 'Input',
  });


  const resetFilters = ()=>{
    setFilters({
      ...INITIAL_FILTERS,
      Mineral: selectedMineral ? [selectedMineral] : [],
    });
    setFlowSelection({ dim: null, value: null });
    setChartSettings({
      breakdownBy: 'Input',
    });
  };
  const exportToPdf = () => {
    setIsExportingPdf(true);
    const input = document.querySelector('.container');
    if (!input) {
      setIsExportingPdf(false);
      return;
    }

    // Use setTimeout to wait for any pending re-renders (like charts) to complete.
    // This solves the race condition where we measure the page before it's fully drawn.
    setTimeout(() => {
      html2canvas(input, {
        backgroundColor: '#0b0b0b',
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX
      }).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = 595.28; // A4 width in pt
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'pt',
          format: [pdfWidth, pdfHeight]
        });

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save('critical-minerals-dashboard.pdf');
      }).catch(err => {
        console.error("Error exporting PDF:", err);
        alert("An error occurred while exporting to PDF.");
      }).finally(() => {
        setIsExportingPdf(false);
      });
    }, 500); // A 500ms delay is usually sufficient for charts to render.
  };

  const handleMineralSelect = (mineralName) => {
    setSelectedMineral(mineralName);
    // Clear previous data before setting new filters
    setRaw([]);
    setBenchmarkRows([]);
    setError(null);
    setFilters(prevFilters => ({
      ...INITIAL_FILTERS,
      Mineral: [mineralName]
    }));
  };

  const handleBackToLanding = () => {
    setSelectedMineral(null);
    setFilters(INITIAL_FILTERS);
    setRaw([]);
    setBenchmarkRows([]);
    setError(null);
  };

  useEffect(()=>{ (async ()=>{
    try{
      setLoading(true);
      const [dimsData, flowData] = await Promise.all([
        fetchDims(),
        fetchTungstenFlows()
      ]);

      // Add Scenario options if not present, with Baseline as default
      if (dimsData.Scenario && !dimsData.Scenario.includes('Baseline')) {
        dimsData.Scenario.unshift('Baseline');
      } else if (!dimsData.Scenario) {
        dimsData.Scenario = ['Baseline'];
      }

      if (dimsData.performer) {
        dimsData.Performer = dimsData.performer;
        delete dimsData.performer;
      }

      setDims(dimsData);
      setFlows(addCountryDataToFlows(flowData));
      setError(null);
    }
    catch(e){ setError(String(e)); }
    // No longer setting loading to false here, it will be set after the first data fetch
  })(); },[]);
  
  useEffect(() => {
    (async () => {
      // Don't fetch data until a mineral is selected AND dims are loaded
      if (!selectedMineral || Object.values(dims).every(arr => arr.length === 0)) return;

      try {
        setLoading(true);

        let data, benchmarkData;

        if (filters.Scenario) {
          const historicalFilters = { ...filters, Scenario: '', Type: ['Historical'] };
          const [
            scenarioData,
            scenarioBenchmarkData,
            histData,
            histBenchmarkData
          ] = await Promise.all([
            fetchTA1(filters, chartSettings, false),
            fetchTA1(filters, chartSettings, true),
            fetchTA1(historicalFilters, chartSettings, false),
            fetchTA1(historicalFilters, chartSettings, true),
          ]);
          data = [...scenarioData, ...histData];
          benchmarkData = [...scenarioBenchmarkData, ...histBenchmarkData];
        } else {
          [data, benchmarkData] = await Promise.all([
            fetchTA1(filters, chartSettings, false),
            fetchTA1(filters, chartSettings, true)
          ]);
        }

        const processData = (d) => d.map(r => ({
          ...r,
          // Map backend snake_case to frontend PascalCase
          "Mineral": r.mineral,
          "Region": r.region,
          "Country": r.country,
          "Form": r.form,
          "Input": r.input,
          "Scenario": r.scenario || 'Baseline',
          "Performer": r.performer,
          "Type": r.type,
          "Units": r.units,
          "Unit Type": r.unit_type,
          "Year": r.year,
          "Avg Value": r.avg_value,
          "Median": r.median_value,
          "10th Percentile": r.p10_value,
          "90th Percentile": r.p90_value,
          "Price Type": r.price_type,
          "Value Type": r.value_type,
          // other value fields can be mapped here
        }));

        const processedData = processData(data);
        const processedBenchmarkData = processData(benchmarkData);

        // Set both datasets together to avoid timing issues
        setRaw(processedData);
        setBenchmarkRows(processedBenchmarkData);
        setError(null);
      } catch (e) {
        setError(String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [filters, dims, chartSettings, selectedMineral]);

  const years = useMemo(()=> {
    const allYears = new Set();
    if (dims.Year) {
        dims.Year.forEach(y => allYears.add(y));
    }
    const flowYears = flows.map(f=>f.year).filter(Number.isFinite);
    flowYears.forEach(y => allYears.add(y));
    return Array.from(allYears).sort((a,b)=>a-b);
  }, [dims, flows]);

  // `filtered` is now just `raw` as the API returns filtered data.
  const filtered = raw; 
  
  const filteredFlows = useMemo(()=>applyFlowFilters(flows, filters, flowSelection), [flows, filters, flowSelection]);
  const noCountryFilter = useMemo(() => filters.Country.length === 0, [filters.Country]);

  if (!selectedMineral) {
    return <LandingPage onMineralSelect={handleMineralSelect} />;
  }

  return (
    <>
      {isExportingPdf && <div className="export-overlay"><div>Generating PDF, please wait...</div></div>}
      {loading && <div className="loading-bar"></div>}
      <header className="header">
        <div className="brand">
          <div className="logo"></div>
          <div className="title">Critical Minerals Forum</div>
          {selectedMineral && (
            <MineralDropdown 
              selectedMineral={selectedMineral}
              onMineralSelect={handleMineralSelect}
              onBackToLanding={handleBackToLanding}
            />
          )}
        </div>
        <div className="header-controls">
          <button onClick={exportToPdf}>Export</button>
          <button onClick={resetFilters}>Reset Filters</button>
        </div>
      </header>

      <main className="container">
        <TopFilterBar dims={dims} yearOptions={years} filters={filters} onFiltersChange={setFilters} />
        {error && <div className="panel error-panel">Error: {error}</div>}
        <div className="layout">
          <DataTable rows={filtered} allRows={raw} defaultDim="Input" filters={filters} onFiltersChange={setFilters} />
          <StackedBarChart 
            rows={filtered} 
            allRows={benchmarkRows} 
            noCountryFilter={noCountryFilter}
            filters={filters}
            onFiltersChange={setFilters}
            settings={chartSettings}
            onSettingsChange={setChartSettings}
          />
        </div>
        <div className="layout">
          <FlowTable 
            flows={filteredFlows} 
            defaultDim="Mine" 
            defaultMetric="value"
            selection={flowSelection}
            onSelectionChange={setFlowSelection}
          />
          <FlowMap flows={filteredFlows} />
        </div>
      </main>
      <footer className="footer">We are building resilient and diversified supply chains…</footer>
    </>
  );
}
